import { useEffect, useState } from "react";
import client from "@/lib/api";

// Module-level cache so pickers render instantly on every open; refreshed in the background.
const optCache = { orgs: null, contacts: null, props: null, at: 0, inflight: null };
const subscribers = new Set();
function refreshOptions() {
  if (optCache.inflight) return optCache.inflight;
  const one = (key, req) => req.then((r) => { optCache[key] = r.data; subscribers.forEach((f) => f()); }).catch(() => {});
  optCache.inflight = Promise.all([
    one("props", client.get("/organisations", { params: { lite: true, org_type: "DTR Property" } })),
    one("orgs", client.get("/organisations", { params: { lite: true } })),
    one("contacts", client.get("/contacts", { params: { lite: true } })),
  ]).finally(() => { optCache.at = Date.now(); optCache.inflight = null; });
  return optCache.inflight;
}
export const invalidateOptions = () => { optCache.at = 0; };
export const warmOptions = () => refreshOptions();

export function useOptions(enabled = true) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const f = () => force((n) => n + 1);
    subscribers.add(f);
    if (Date.now() - optCache.at > 3000) refreshOptions();
    return () => subscribers.delete(f);
  }, [enabled]);
  return { orgs: optCache.orgs || [], contacts: optCache.contacts || [], props: optCache.props || [] };
}

// `search` = long list: MultiSelect waits for 3+ typed letters before listing matches
export const orgOptions = (orgs) => orgs.map((o) => ({ value: o.id, label: o.name, search: true }));
export const nonPropertyOrgOptions = (orgs) => orgOptions(orgs.filter((o) => o.org_type !== "DTR Property"));
// `dtr` marks property options so MultiSelect shows the "Select Group" menu
// Pass `props` (properties-only list) from useOptions; filtering keeps it safe if given all orgs.
export const propertyOptions = (orgs) => orgs.filter((o) => o.org_type === "DTR Property")
  .map((o) => ({ value: o.id, label: o.name, dtr: (o.property_type || "DTR Property") === "DTR Property" }));
export const propOptions = (props, orgId) =>
  props
    .filter((p) => !orgId || p.organisation_id === orgId)
    .map((p) => ({ value: p.id, label: p.name }));
export const contactOptions = (contacts, orgId) =>
  contacts
    .filter((c) => !orgId || c.organisation_id === orgId)
    .map((c) => ({ value: c.id, label: `${c.first_name} ${c.last_name || ""}`.trim(), search: true }));
export const userOptions = (users) => users.map((u) => ({ value: u.id, label: u.name }));

// Reuse an existing similar record instead of creating a duplicate, if the user agrees
async function existingMatch(entity, fields, kind) {
  try {
    const { data } = await client.post("/duplicate-check", { entity, fields });
    const m = (data.matches || [])[0];
    if (m && window.confirm(`A similar ${kind} already exists: "${m.label}".\n\nOK = use the existing one\nCancel = create a new one anyway`)) return { value: m.id, label: m.label };
  } catch (e) { /* fall through to create */ }
  return null;
}

export const quickAddOrg = async (name) => {
  invalidateOptions();
  const hit = await existingMatch("organisation", { name }, "organisation");
  if (hit) return hit;
  const { data } = await client.post("/organisations", { name, org_type: "Partner", relationship_status: "N/A", relationship_health: "N/A" });
  return { value: data.id, label: data.name };
};

export const quickAddContact = async (name) => {
  invalidateOptions();
  const [first, ...rest] = name.trim().split(" ");
  const hit = await existingMatch("contact", { first_name: first, last_name: rest.join(" ") }, "contact");
  if (hit) return hit;
  const { data } = await client.post("/contacts", { first_name: first, last_name: rest.join(" ") || null });
  return { value: data.id, label: `${data.first_name} ${data.last_name || ""}`.trim() };
};

export const quickAddSupplier = async (name) => {
  invalidateOptions();
  const hit = await existingMatch("organisation", { name }, "organisation");
  if (hit) return hit;
  const { data } = await client.post("/organisations", { name, org_type: "Supplier", relationship_status: "N/A", relationship_health: "N/A" });
  return { value: data.id, label: data.name };
};
